import React, { useState } from "react";
import { Plus, Loader2, Flag, Paperclip, Download } from "lucide-react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatDate } from "./constants";

export function NotesTab({ contractId, notes, fetchData, expanded, canAddNote }) {
  const [form, setForm] = useState({ content: "", category: "general", is_flagged: false });
  const [files, setFiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiClient.post(`/contracts/${contractId}/notes`, null, {
        params: { content: form.content, category: form.category, is_flagged: form.is_flagged },
      });
      const noteId = res.data?.id;
      if (noteId && files.length > 0) {
        for (const file of files) {
          const fd = new FormData();
          fd.append("file", file);
          await apiClient.post(`/contracts/${contractId}/notes/${noteId}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      }
      toast.success("Note saved" + (files.length > 0 ? ` with ${files.length} attachment(s)` : ""));
      setForm({ content: "", category: "general", is_flagged: false });
      setFiles([]);
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{notes.length} note(s)</p>
        {canAddNote && (
          <Button size="sm" variant="outline" className="gap-1 rounded-sm" onClick={() => setShowForm(true)} data-testid="add-note-btn"><Plus size={14} />Add Note</Button>
        )}
      </div>

      {showForm && (
        <Card className="border-cyan-200">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["general", "risk", "action", "escalation", "relationship"].map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-0.5">
                <Switch checked={form.is_flagged} onCheckedChange={(v) => setForm({ ...form, is_flagged: v })} />
                <Label className="text-xs">Flag for review</Label>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Note</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={3} className="rounded-sm" placeholder="Enter your note..." data-testid="note-content-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><Paperclip size={12} />Attachments (Evidence)</Label>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-slate-300 rounded-sm text-xs text-slate-500 hover:bg-slate-50 transition-colors" data-testid="note-attach-btn">
                  <Paperclip size={13} />
                  {files.length > 0 ? `${files.length} file(s) selected` : "Attach files"}
                  <input type="file" multiple className="hidden" accept=".pdf,.doc,.docx,.xlsx,.jpg,.jpeg,.png,.webp,.txt,.csv" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                </label>
                {files.length > 0 && (
                  <button className="text-xs text-red-500 hover:text-red-600" onClick={() => setFiles([])}>Clear</button>
                )}
              </div>
              {files.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {files.map((f, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px] gap-1">
                      <Paperclip size={9} />{f.name} <span className="text-slate-400">({(f.size / 1024).toFixed(0)}KB)</span>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setFiles([]); }}>Cancel</Button>
              <Button size="sm" disabled={saving || !form.content} data-testid="save-note-btn" onClick={handleSave}>{saving ? <Loader2 size={14} className="animate-spin" /> : "Save"}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className={`space-y-2 overflow-y-auto ${expanded ? "max-h-[calc(100vh-350px)]" : "max-h-[400px]"}`}>
        {notes.map((note) => (
          <div key={note.id} className={`p-3 border rounded-sm ${note.is_flagged ? "border-amber-300 bg-amber-50/30 dark:bg-amber-950/10" : ""}`}>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-xs">{note.category}</Badge>
              {note.is_flagged && <Flag size={12} className="text-amber-500" />}
              {note.attachments?.length > 0 && (
                <Badge variant="secondary" className="text-[10px] gap-1"><Paperclip size={9} />{note.attachments.length}</Badge>
              )}
            </div>
            <p className="text-sm">{note.content}</p>
            {note.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {note.attachments.map((att) => (
                  <a
                    key={att.file_id}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/note-attachments/${att.file_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 border rounded-sm text-[11px] text-cyan-700 bg-cyan-50 hover:bg-cyan-100 transition-colors"
                    data-testid={`attachment-${att.file_id}`}
                  >
                    <Download size={11} />
                    {att.filename}
                    {att.size && <span className="text-slate-400">({(att.size / 1024).toFixed(0)}KB)</span>}
                  </a>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-400 mt-1">{note.created_by_name} · {formatDate(note.created_at)}</p>
          </div>
        ))}
        {notes.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No notes yet</p>}
      </div>
    </div>
  );
}
